import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { EventBus, EVENTS } from "src/services/eventEmitter";
import { useAISidebar } from "src/hooks/useAISidebar";
import Url, { url } from "src/consts/urlPaths";
import { AGENTCORE_NAMES } from "../../consts";
import streamAIResponse from "../../utils/streamAIResponse";
import { CHAT_PHASE } from "./useAIChatPhase";

const uuidv4 = () => crypto.randomUUID();

const TOOLS_USED_RE = /<tools-used\s+value="(\d+)">/;
const TOOLS_USED_STRIP_RE = /<tools-used\s+value="\d+">/g;

const DASHBOARD_V2_PATH_RE = /\/dashboards\/v2\/([^/?#]+)/;

/**
 * Extracts and strips `<tools-used value="N">` from raw content.
 * @returns {{ displayContent: string|null, toolsUsed: number|null }}
 */
function parseToolsUsed(rawContent) {
  if (!rawContent) {
    return { displayContent: null, toolsUsed: null };
  }

  const match = rawContent.match(TOOLS_USED_RE);
  const toolsUsed = match ? parseInt(match[1], 10) : null;
  const displayContent = rawContent.replace(TOOLS_USED_STRIP_RE, "").trim();
  return { displayContent, toolsUsed };
}

// Builds an optimistic in-thread assistant error message from a terminal
// pipeline failure event, mirroring the structured message the backend
// persists (so live feedback matches what reloads from the DB).
function buildErrorMessage(event, chatId) {
  const isCancelled = event?.type === "pipeline_cancelled";
  const metadata = event?.metadata || {};
  const step = event?.step || "unknown";
  const reason = isCancelled
    ? event?.reason || "The dashboard build was stopped."
    : event?.error || "An unexpected error occurred.";
  const diagnostics = event?.debug?.diagnostics || [];

  return {
    id: `error-${step}-${Date.now()}`,
    aiChatId: chatId,
    role: "assistant",
    status: "error",
    content: `**Dashboard build failed** at step \`${step}\`.\n\n${reason}`,
    errorDetails: {
      kind: isCancelled ? "pipeline_cancelled" : "pipeline_error",
      step,
      code: metadata.code || event?.code || null,
      reason,
      errors: metadata.errors || event?.errors || [],
      diagnostics,
    },
  };
}

/**
 * Hook that wraps `streamAIResponse` and drives a `useAIChatPhase` state
 * machine automatically.
 *
 * Handles both Q&A streaming (message chunks) and the data-driven dashboard
 * pipeline (pipeline_* events fanned out on EventBus + `new_dashboard`
 * navigation to the full canvas). Owns the AbortController for cancellation.
 *
 * @returns {{
 *   send: (prompt: string, opts?: { chatId?: string, isRetry?: boolean }) => Promise<void>,
 *   cancel: () => void,
 *   resetPipeline: () => void,
 *   toolsUsedCount: number,
 *   pipelineSteps: Object,
 * }}
 */
export function useAIChatStream({
  transition,
  tenantId,
  apiUrl,
  workspaceId,
  pitDate,
  getToken,
  adoptChatId,
  updateChatOptimistically,
  refetchChats,
  selectedAgent,
  onStreamComplete,
}) {
  const [toolsUsedCount, setToolsUsedCount] = useState(0);
  const [pipelineSteps, setPipelineSteps] = useState({});
  const abortRef = useRef(null);
  // Pending "auto-clear the checklist" timer. Tracked so a new build started
  // within the clear delay isn't wiped by a previous build's timeout.
  const clearTimerRef = useRef(null);
  // Track the chatId the backend returns (may differ from what we sent)
  const responseChatIdRef = useRef(null);
  // Track whether we've seen the first content chunk (for thinking → streaming)
  const hasStreamedRef = useRef(false);
  // Track the last message content for onStreamComplete
  const lastMessageRef = useRef(null);
  // A terminal pipeline failure (error/cancelled) already wrote an in-thread
  // error message; the trailing stream_complete → onDone must not overwrite it
  // with a normal "done" turn.
  const terminalFailureRef = useRef(false);

  const history = useHistory();
  // Keep the latest location so stream callbacks (which fire well after send)
  // read the path at the moment they run, not a stale closure.
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;

  const { updateSidebarState, versionId } = useAISidebar();

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    transition(CHAT_PHASE.IDLE);
  }, [transition]);

  const cancelPipelineClear = useCallback(() => {
    if (clearTimerRef.current) {
      clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }
  }, []);

  // Auto-clear the checklist after a terminal pipeline event, cancelling any
  // prior pending clear so timers from earlier builds can't fire mid-build.
  const schedulePipelineClear = useCallback(() => {
    cancelPipelineClear();
    clearTimerRef.current = setTimeout(() => {
      setPipelineSteps({});
      clearTimerRef.current = null;
    }, 3000);
  }, [cancelPipelineClear]);

  // Clear the pipeline checklist when the conversation context changes (new
  // chat, session clear, thread switch) so a prior build's progress doesn't
  // bleed into an unrelated thread.
  const resetPipeline = useCallback(() => {
    cancelPipelineClear();
    setPipelineSteps({});
  }, [cancelPipelineClear]);

  useEffect(() => cancelPipelineClear, [cancelPipelineClear]);

  const send = useCallback(
    async (prompt, { chatId = null, isRetry = false } = {}) => {
      // Reset per-request state
      responseChatIdRef.current = null;
      hasStreamedRef.current = false;
      lastMessageRef.current = null;
      terminalFailureRef.current = false;
      setToolsUsedCount(0);
      cancelPipelineClear();
      setPipelineSteps({});

      // Cancel any in-flight request
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      transition(CHAT_PHASE.CONNECTING);

      // Optimistically show the user message (skip on retry — it already exists)
      if (!isRetry) {
        updateChatOptimistically({
          id: `temp-user-${uuidv4()}`,
          role: "user",
          content: prompt,
        });
      }

      // When docked on an existing dashboard, pass its id so the switchboard
      // biases toward the dashboard route and the pipeline runs in edit mode.
      // Omit for "new" (create mode is signalled by the absence of an id).
      const dashboardId =
        locationRef.current?.pathname?.match(DASHBOARD_V2_PATH_RE)?.[1];
      const dashboardPayload =
        dashboardId && dashboardId !== "new"
          ? { dashboard_id: dashboardId }
          : {};

      try {
        const token = await getToken();

        await streamAIResponse(
          { prompt, aiChatId: chatId, ...dashboardPayload },
          {
            tenantId,
            apiUrl,
            agentName:
              selectedAgent === AGENTCORE_NAMES.GQL_EXPERT_V2
                ? AGENTCORE_NAMES.ASK_TEAMFORM_V2
                : selectedAgent,
            signal: controller.signal,
            headers: {
              Authorization: `Bearer ${token}`,
              "x-workspace-id": workspaceId,
              "x-pit-date": pitDate,
              ...(selectedAgent === AGENTCORE_NAMES.GQL_EXPERT_V2 && {
                "X-Agent-Select": "gql_expert",
              }),
            },

            onConnectionEstablished: (data) => {
              transition(CHAT_PHASE.THINKING);
              if (data?.aiChatId && data.aiChatId !== chatId) {
                responseChatIdRef.current = data.aiChatId;
                adoptChatId(data.aiChatId);
              }
            },

            onError: (e) => {
              // Any fatal error means the trailing onDone must not mark the
              // turn "done" over the error UI.
              terminalFailureRef.current = true;
              // Terminal build failures surface in-thread (like the persisted
              // message), not as a banner; notify the definition provider so it
              // clears its streaming flag.
              if (
                e?.type === "pipeline_error" ||
                e?.type === "pipeline_cancelled"
              ) {
                updateChatOptimistically(
                  buildErrorMessage(e, responseChatIdRef.current ?? chatId)
                );
                EventBus.emit(EVENTS.AI_DASHBOARD_BUILD_PROGRESS, e);
                // Fade the checklist like the other terminal handlers so a
                // failed build doesn't leave a stale progress list.
                schedulePipelineClear();
                transition(CHAT_PHASE.IDLE);
                return;
              }
              // Non-pipeline fatal errors (429, network drop, "Stream ended
              // early.") never emit a terminal pipeline_* event, so a build's
              // definition `streaming` would stay stuck. Signal it defensively.
              EventBus.emit(EVENTS.AI_DASHBOARD_BUILD_PROGRESS, {
                type: "pipeline_error",
              });
              // Map rate-limit responses to friendly retry guidance instead of
              // a raw "Request failed with status 429".
              if (e?.status === 429) {
                const mins = e.retryAfter ? Math.ceil(e.retryAfter / 60) : null;
                transition(
                  CHAT_PHASE.ERROR,
                  new Error(
                    mins
                      ? `Rate limit reached. Try again in ${mins} minutes.`
                      : "Rate limit reached. Please try again later."
                  )
                );
                return;
              }
              transition(CHAT_PHASE.ERROR, e);
            },

            onMessageChunk: (chunkEvent) => {
              if (!chunkEvent?.message) {
                return;
              }

              // Adopt chatId from first chunk if not already adopted
              if (!responseChatIdRef.current && chunkEvent.message.aiChatId) {
                responseChatIdRef.current = chunkEvent.message.aiChatId;
              }

              const rawContent =
                chunkEvent.content || chunkEvent.message.content;
              const { displayContent, toolsUsed } = parseToolsUsed(rawContent);

              if (toolsUsed !== null) {
                setToolsUsedCount(toolsUsed);
              }

              // Skip optimistic update for tool-count-only chunks
              if (!displayContent && toolsUsed !== null) {
                return;
              }

              // First real content chunk → transition to streaming
              if (!hasStreamedRef.current && displayContent) {
                hasStreamedRef.current = true;
                transition(CHAT_PHASE.STREAMING);
              }

              updateChatOptimistically({
                ...chunkEvent.message,
                content: displayContent,
              });
              lastMessageRef.current = {
                ...chunkEvent.message,
                content: displayContent,
              };
            },

            onMessage: (messageEvent) => {
              if (messageEvent?.final_message) {
                updateChatOptimistically(messageEvent.final_message);
                lastMessageRef.current = messageEvent.final_message;
              }
            },

            // -- Dashboard pipeline events (data-driven builds) --
            onNewDashboard: () => {
              // A brand-new build started; make sure the pane is visible and
              // move it to the full canvas unless we are already on a v2
              // dashboard route (page-initiated build).
              updateSidebarState(true, versionId);
              if (!locationRef.current?.pathname?.includes("/dashboards/v2/")) {
                history.push(url(Url.AI_DASHBOARDS_V2, { dashboardId: "new" }));
              }
            },

            onPipelineStep: (data) => {
              EventBus.emit(EVENTS.AI_DASHBOARD_BUILD_STEP_DEBUG, data.debug);
              setPipelineSteps((prev) => ({
                ...prev,
                [data.step]: data.status,
              }));
              EventBus.emit(EVENTS.AI_DASHBOARD_BUILD_PROGRESS, data);
            },

            onPipelineComplete: (data) => {
              EventBus.emit(EVENTS.AI_DASHBOARD_BUILD_PROGRESS, {
                ...data,
                type: "pipeline_complete",
              });
              schedulePipelineClear();
            },

            onPipelineCancelled: (data) => {
              terminalFailureRef.current = true;
              if (data?.step) {
                setPipelineSteps((prev) => ({
                  ...prev,
                  [data.step]: "error",
                }));
              }
              updateChatOptimistically(
                buildErrorMessage(data, responseChatIdRef.current ?? chatId)
              );
              EventBus.emit(EVENTS.AI_DASHBOARD_BUILD_PROGRESS, {
                ...data,
                type: "pipeline_cancelled",
              });
              schedulePipelineClear();
              // The trailing stream_complete → onDone now early-returns on a
              // terminal failure, so settle the phase here; otherwise
              // isStreaming stays true and input/nav guards stay stuck.
              transition(CHAT_PHASE.IDLE);
            },

            onDashboardPersisted: (data) => {
              EventBus.emit(EVENTS.AI_DASHBOARD_BUILD_PROGRESS, {
                ...data,
                type: "dashboard_persisted",
              });
              schedulePipelineClear();
            },

            onTemplateChunk: (data) => {
              EventBus.emit(EVENTS.AI_DASHBOARD_BUILD_PROGRESS, {
                ...data,
                type: "layout_chunk",
              });
            },

            onDone: (finalMessage) => {
              // Adopt chat + refresh list if backend created a new chat
              if (
                responseChatIdRef.current &&
                responseChatIdRef.current !== chatId
              ) {
                adoptChatId(responseChatIdRef.current);
                refetchChats();
              }
              setToolsUsedCount(0);

              // A terminal failure already wrote an in-thread error message and
              // settled the phase; don't overwrite it with a "done" turn.
              if (terminalFailureRef.current) {
                return;
              }

              transition(CHAT_PHASE.DONE);

              // Use the backend's reconciled final message if available,
              // otherwise fall back to the last chunk/message we saw.
              const resolved = finalMessage ?? lastMessageRef.current;
              if (resolved) {
                updateChatOptimistically({
                  ...resolved,
                  status: "done",
                });
              }

              // Notify caller (e.g. for dashboard tag detection)
              onStreamComplete?.(resolved);
            },
          }
        );
      } catch (e) {
        // fetch itself threw (network error, not an SSE error event)
        if (!controller.signal.aborted) {
          EventBus.emit(EVENTS.AI_DASHBOARD_BUILD_PROGRESS, {
            type: "pipeline_error",
          });
          transition(CHAT_PHASE.ERROR, e);
        }
      }
    },
    [
      transition,
      tenantId,
      apiUrl,
      workspaceId,
      pitDate,
      getToken,
      adoptChatId,
      updateChatOptimistically,
      refetchChats,
      selectedAgent,
      onStreamComplete,
      history,
      updateSidebarState,
      versionId,
      cancelPipelineClear,
      schedulePipelineClear,
    ]
  );

  return { send, cancel, resetPipeline, toolsUsedCount, pipelineSteps };
}
